/* global React */

// Resolves to an inlined blob URL when exported standalone;
// falls back to the on-disk path otherwise.
const R = (id, path) => (typeof window !== 'undefined' && window.__resources && window.__resources[id]) || path;

// ─────────────────────────────────────────────────────────────
// TOSY real photography — shot at 111 Long Lane, SE1
// ─────────────────────────────────────────────────────────────
const IMG = {
  // Exterior / signage
  exterior:    R('exterior', 'img/untitled.webp'),        // full frontage, blue sign, awning
  exteriorSign:'img/untitled-5.webp',      // sign + hen party on terrace

  // Terrace
  terrace:     'img/untitled-5.webp',
  terraceHens: R('terraceHens', 'img/untitled-8.webp'),      // 4 hens in blue at the rail

  // Singing / crowd
  // (no singing1 — untitled-136, the beard + white-shirt guys, was rejected by
  // the client. It is excluded from build-images.mjs so it cannot ship.)
  singing3:    R('singing3', 'img/untitled-99.webp'),     // packed room, arms up
  singing4:    R('singing4', 'img/untitled-101.webp'),    // solo singer, hand raised
  singing5:    R('singing5', 'img/untitled-63.webp'),     // white-shirt guy mid-note
  singing6:    R('singing6', 'img/untitled-79.webp'),     // duo belting under the red LED

  // Cocktails (stock until real menu photography arrives)
  cocktailRed: R('cocktailRed', 'https://images.unsplash.com/photo-1536935338788-846bb9981813?w=900&q=80'),
  cocktailPour:R('cocktailPour', 'https://images.unsplash.com/photo-1551538827-9c037cb4f32a?w=900&q=80'),
  cocktail2:   R('cocktail2', 'https://images.unsplash.com/photo-1514362545857-3bc16c4c7d1b?w=900&q=80'),
  cocktail3:   R('cocktail3', 'https://images.unsplash.com/photo-1470337458703-46ad1756a187?w=900&q=80'),
  class2:      R('class2', 'https://images.unsplash.com/photo-1574096079513-d8259312b785?w=900&q=80'),
  class3:      R('class3', 'https://images.unsplash.com/photo-1543007630-9710e4a00a20?w=900&q=80'),
  outsideFront:R('outsideFront', 'img/untitled-5.webp'),   // terrace frontage + sign
  outsideGolden:'img/outside-front-golden.webp',           // golden-hour terrace, from the desktop site

  // Real venue cocktail shots (no stock)
  cocktailReal:'img/untitled-141.webp',                    // cocktail at the bar
  pourReal:    'img/untitled-144.webp',                    // pour behind the bar
  cocktailFactory:'img/cocktail-factory.webp',            // the "Factory" neon
  cocktailsComic:'img/cocktails-comic-table.webp',        // round of cocktails on the comic table
  martiniClassic:'img/cocktail-martini.webp',              // classic martini w/ olive (site)
  barmanBigHair:'img/barman-bighair.webp',                 // big-hair barman garnishing (site)

  // Karaoke room shots for the karaoke sheet
  roomWood:    'img/untitled-19.webp',     // mics, screen, plywood + caged bar
  roomInUse:   'img/untitled-24.webp',     // people picking songs
  songScreen:  'img/untitled-31.webp',     // song-select touchscreen
  crowdCheers: 'img/untitled-73.webp',     // whole room pointing, pints up
  singing8:    'img/untitled-97.webp',     // pair sharing a mic, mid-note
};

// Alt text, keyed by path so PhotoCycle can look it up from `srcs` alone.
// Wording comes from the descriptions already noted against each photo above —
// these are real shots of the venue, so they're worth being findable, and 11 of
// the 13 images on the page previously had an empty alt.
const ALT = {
  [IMG.exterior]:       'The Old School Yard frontage on Long Lane, blue sign lit above the awning',
  [IMG.exteriorSign]:   'The blue Old School Yard sign above the terrace, a hen party underneath',
  [IMG.terraceHens]:    'A hen party in blue at the rail of the heated terrace',
  [IMG.singing3]:       'A packed karaoke room at The Old School Yard, arms in the air mid-song',
  [IMG.singing4]:       'A singer with a hand raised in one of the private karaoke rooms',
  [IMG.singing5]:       'A singer mid-note in a private karaoke room',
  [IMG.singing6]:       'Two people belting a duet under the red LED of a karaoke room',
  [IMG.singing8]:       'A pair sharing a microphone mid-note',
  [IMG.outsideGolden]:  'The Old School Yard terrace at golden hour',
  [IMG.outsideFront]:   'The terrace frontage and sign on Long Lane',
  [IMG.cocktailReal]:   'A cocktail on the bar at The Old School Yard',
  [IMG.pourReal]:       'A cocktail being poured behind the bar',
  [IMG.cocktailsComic]: 'A round of cocktails on the comic-print table',
  [IMG.martiniClassic]: 'A classic martini with an olive',
  [IMG.barmanBigHair]:  'A bartender garnishing a cocktail behind the bar',
  [IMG.cocktailFactory]:'The neon "Cocktail Factory" sign',
  [IMG.roomWood]:       'A private karaoke room — microphones, song screen and plywood walls',
  [IMG.roomInUse]:      'A group picking their next song in a karaoke room',
  [IMG.songScreen]:     'The touchscreen used to search and queue songs in each room',
  [IMG.crowdCheers]:    'A full karaoke room with pints raised',
};
window.TOSY_ALT = ALT;

// Crop focus, for photos whose subject isn't in the middle of the frame.
// `object-fit: cover` keeps the centre band, and a tall shot dropped into a wide
// box keeps very little of it — untitled-5 is a 1200x1800 portrait in a 2.2:1
// box, so the centre 30% was all awning and brick, with the terrace and the
// people cropped clean off the bottom. Percentages are the point in the image
// that gets aligned, so a high number pulls the crop downwards.
const FOCUS = {
  [IMG.outsideFront]: 'center 88%',   // hen party, railing and string lights sit low
  [IMG.pourReal]:     'center 28%',   // the hand, straws and garnish are up top
};
window.TOSY_FOCUS = FOCUS;

// Event artwork (auto-hides when the event has passed)
const EVENT = {
  live: true,
  slug: 'la-escuelita',
  name: 'La Escuelita',
  kicker: 'THE OLD SCHOOL YARD PRESENTS',
  date: '2026-08-15',            // set in the past (or live:false) to hide the tile
  dateLabel: 'Sat 15 August',
  doors: 'Doors 5:30pm · Last dance 12:30am',
  price: '£10 before 9pm, incl. a drink',
  priceLate: '£15 anytime, incl. a drink',
  genres: 'Latin pop · Salsa · Reggaeton · Soca · Dancehall',
  extras: 'BBQ from Chefologist · Sangria · Margaritas',
  note: 'Limited number available · Over 18s, ID required',
  blurb: 'Our first ever Latin night. Latin pop, salsa, reggaeton, soca and dancehall — Shakira, Jennifer Lopez, Ricky Martin, Marc Anthony, all the ones you know every word to.',
  url: 'https://bookings.theoldschoolyard.com/book/tosy/la-escuelita-tickets',  // ticket widget deep link
  square: R('evSquare', encodeURI('img/laescue insta/la-escuelita-square-1080x1080.webp')),
  poster: R('evPoster', encodeURI('img/laescue insta/la-escuelita-saturday-1080x1350.webp')),
};
// true only while the event is still ahead of us
EVENT.upcoming = EVENT.live && new Date(EVENT.date + 'T23:59:59') >= new Date();
window.TOSY_EVENT = EVENT;

// Photo sets — each tile crossfades through its own short run
const SETS = {
  stage:    [IMG.singing3, IMG.singing4, IMG.singing6, IMG.singing5],
  cocktails:[IMG.cocktailsComic, IMG.cocktailReal],
  masterclass:[IMG.pourReal, IMG.martiniClassic, IMG.barmanBigHair],
  terrace:  [IMG.outsideGolden],
  terrace2: [IMG.terraceHens, IMG.outsideFront],
};
window.TOSY_SETS = SETS;

window.VH_IMG = IMG;
window.TOSY_IMG = IMG;

// ─────────────────────────────────────────────────────────────
// Music toggle playlist — tapping the waveform on the karaoke
// tile plays a random track from this list (tap again to stop).
// DROP LICENSED MP3s INTO uploads/music/ AND LIST THEM HERE.
// Wanted at launch (pending licensing): Holding Out for a Hero,
// The Love You Save, Sandstorm, Who's That Girl, Macarena.
// Leave empty and the waveform stays a plain animation.
// ─────────────────────────────────────────────────────────────
window.TOSY_TRACKS = [
  // 'uploads/music/holding-out-for-a-hero.mp3',
  // 'uploads/music/the-love-you-save.mp3',
  // 'uploads/music/sandstorm.mp3',
  // 'uploads/music/whos-that-girl.mp3',
  // 'uploads/music/macarena.mp3',
];

// Ken-burns photo with tone control
function Photo({ src, alt, tone = 0.45, dir = 0, style = {}, children }) {
  const anim = dir === 0 ? 'none' : `kb-${dir > 0 ? 'in' : 'out'} 22s ease-in-out infinite alternate`;
  // Falls back to the shared ALT map, so a caller that passes no alt still gets
  // a real description rather than an empty one.
  const label = alt !== undefined ? alt : ((window.TOSY_ALT && window.TOSY_ALT[src]) || '');
  return (
    <div style={{ position: 'relative', width: '100%', height: '100%', overflow: 'hidden', background: '#0a0410', ...style }}>
      <img src={src} alt={label} loading="lazy" style={{
        position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover',
        objectPosition: (window.TOSY_FOCUS && window.TOSY_FOCUS[src]) || 'center',
        filter: 'brightness(0.78) contrast(1.06) saturate(1.05)', animation: anim,
      }} />
      <div style={{ position: 'absolute', inset: 0, background: `linear-gradient(to top, rgba(8,6,20,${tone + 0.4}) 0%, rgba(8,6,20,${tone * 0.25}) 62%, transparent 88%)` }} />
      {children}
    </div>
  );
}

window.Photo = Photo;


// ─────────────────────────────────────────────────────────────
// PhotoCycle — crossfades through a set, ken-burns on each frame.
// Optional `lyric` prop inserts a karaoke-screen slide at position 0:
// the words light up one by one, exactly like the screen in the room.
// Swipeable; respects prefers-reduced-motion.
// ─────────────────────────────────────────────────────────────
// Lyric-slide timing, in seconds. The words light one at a time like the
// screen in the room, so the slide has to outlive the whole fill — otherwise
// it cuts away moments after the last word lands and nobody gets to read it.
const LYRIC_START = 1.0;   // beat before the first word lights
const LYRIC_STEP  = 0.55;  // gap between words (roughly sung pace)
const LYRIC_FILL  = 0.34;  // how long each word takes to fill pink
const LYRIC_HOLD  = 3.0;   // completed line sits there before we move on

function PhotoCycle({ srcs = [], tone = 0.45, interval = 5000, lyric = null, children }) {
  const [i, setI] = React.useState(0);
  const reduce = React.useRef(typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches);
  const timer = React.useRef(null);
  const touch = React.useRef(null);
  const iRef = React.useRef(0);
  const count = srcs.length + (lyric ? 1 : 0);
  const words = lyric ? lyric.split(' ') : [];

  // Derived from the word animation above, so the two can never drift apart.
  const lyricDwell = lyric
    ? (LYRIC_START + Math.max(0, words.length - 1) * LYRIC_STEP + LYRIC_FILL + LYRIC_HOLD) * 1000
    : 0;
  const dwellFor = n => (lyric && n === 0 ? lyricDwell : interval);

  React.useEffect(() => { iRef.current = i; }, [i]);

  // Self-scheduling timeout rather than a fixed interval, so slide 0 can sit
  // for longer than the photo slides do.
  const startTimer = React.useCallback(() => {
    clearTimeout(timer.current);
    if (count < 2 || reduce.current) return;
    const schedule = () => {
      timer.current = setTimeout(() => {
        const next = (iRef.current + 1) % count;
        iRef.current = next;
        setI(next);
        schedule();
      }, dwellFor(iRef.current));
    };
    schedule();
  }, [count, interval, lyricDwell]);

  React.useEffect(() => { startTimer(); return () => clearTimeout(timer.current); }, [startTimer]);

  // swipe left/right to flick through the photos; auto-cycle resumes after.
  // Native listeners (not React synthetic) so they work identically on all
  // mobile browsers and can preventDefault the follow-up tap.
  const boxRef = React.useRef(null);
  React.useEffect(() => {
    const el = boxRef.current;
    if (!el || count < 2) return;
    const onStart = e => { const t = e.touches[0]; touch.current = { x: t.clientX, y: t.clientY }; };
    const onEnd = e => {
      if (!touch.current) return;
      const t = e.changedTouches[0];
      const dx = t.clientX - touch.current.x;
      const dy = t.clientY - touch.current.y;
      touch.current = null;
      if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy) * 1.5) {
        if (e.cancelable) e.preventDefault();   // a swipe shouldn't also count as a tap on the tile
        e.stopPropagation();
        const next = (iRef.current + (dx < 0 ? 1 : count - 1)) % count;
        iRef.current = next;
        setI(next);
        startTimer();
      }
    };
    el.addEventListener('touchstart', onStart, { passive: true });
    el.addEventListener('touchend', onEnd, { passive: false });
    return () => { el.removeEventListener('touchstart', onStart); el.removeEventListener('touchend', onEnd); };
  }, [count, startTimer]);

  return (
    <div ref={boxRef} data-idx={i} style={{ position: 'relative', width: '100%', height: '100%', overflow: 'hidden', background: '#070d1c', touchAction: 'pan-y' }}>
      {lyric && (
        <div style={{
          position: 'absolute', inset: 0, opacity: i === 0 ? 1 : 0, transition: 'opacity 1.1s ease',
          background: 'radial-gradient(ellipse at 50% 40%, #17204b 0%, #0a1129 55%, #070d1c 100%)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '10%',
        }}>
          {/* words re-key when the slide becomes active so the fill replays */}
          <div key={i === 0 ? 'run' : 'idle'} style={{ textAlign: 'center' }}>
            <div style={{ fontFamily: 'var(--mono)', fontSize: 8, letterSpacing: '0.3em', color: 'rgba(159,208,255,0.6)', marginBottom: 10 }}>♪ NOW SINGING ♪</div>
            <div style={{ fontFamily: 'var(--display)', fontWeight: 800, fontSize: 21, lineHeight: 1.4, letterSpacing: '-0.01em', color: 'rgba(234,242,255,0.45)' }}>
              {words.map((w, n) => (
                <span key={n} style={{
                  display: 'inline-block', margin: '0 3px',
                  animation: i === 0 && !reduce.current ? 'lyric-word ' + LYRIC_FILL + 's ease forwards' : 'none',
                  animationDelay: (LYRIC_START + n * LYRIC_STEP) + 's',
                }}>{w}</span>
              ))}
            </div>
          </div>
        </div>
      )}
      {srcs.map((s, n) => {
        const slide = lyric ? n + 1 : n;
        return (
          <img key={s} src={s} alt={(window.TOSY_ALT && window.TOSY_ALT[s]) || ''} loading={n === 0 ? 'eager' : 'lazy'} style={{
            position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover',
            objectPosition: (window.TOSY_FOCUS && window.TOSY_FOCUS[s]) || 'center',
            filter: 'brightness(0.78) contrast(1.06) saturate(1.05)',
            opacity: slide === i ? 1 : 0, transition: 'opacity 1.1s ease',
            animation: reduce.current ? 'none' : `kb-${n % 2 ? 'out' : 'in'} ${interval * 2}ms ease-in-out infinite alternate`,
          }} />
        );
      })}
      <div style={{ position: 'absolute', inset: 0, background: `linear-gradient(to top, rgba(8,6,20,${tone + 0.4}) 0%, rgba(8,6,20,${tone * 0.25}) 62%, transparent 88%)`, opacity: lyric && i === 0 ? 0.4 : 1, transition: 'opacity 1.1s ease', pointerEvents: 'none' }} />
      {count > 1 && (
        <div style={{ position: 'absolute', top: 10, right: 10, display: 'flex', gap: 3 }}>
          {Array.from({ length: count }).map((_, n) => (
            <span key={n} style={{ width: n === i ? 10 : 4, height: 4, borderRadius: 4, background: n === i ? 'rgba(255,255,255,0.95)' : 'rgba(255,255,255,0.4)', transition: 'width 0.4s, background 0.4s' }} />
          ))}
        </div>
      )}
      {/* overlay content fades out while the lyric screen is showing */}
      <div style={{ position: 'absolute', inset: 0, opacity: lyric && i === 0 ? 0 : 1, transition: 'opacity 1.1s ease', pointerEvents: lyric && i === 0 ? 'none' : 'auto' }}>
        {children}
      </div>
    </div>
  );
}
window.PhotoCycle = PhotoCycle;
